import { delay, eventChannel } from 'redux-saga';
import { takeEvery, takeLatest, put, fork, spawn, call, select, take } from 'redux-saga/effects';
import { actions, types } from '../actions/SocialBarActions';
import { types as WixBridgeTypes } from '@wix/restaurants-redux/dist/actions/WixBridgeActions';
import { types as StyleTypes } from '@wix/restaurants-redux/dist/actions/StylesActions';
import _ from 'lodash';
import ThemeDecoder from '../services/ThemeDecoder';
import SocialIcon from '@wix/restaurants-components-css-modules/dist/svg/SocialIcon/SvgSocialIcon';

const SOCIAL_PLATFORMS = ['facebook', 'foursquare', 'yelp', 'tripadvisor', 'twitter', 'instagram'];

const MIN_WINDOW_WIDTH = 180;
const MIN_WINDOW_HEIGHT = 30;
const EDITOR_VIEW_MODE = 'editor';

const DEFAULT_THROTTLE = 200;

const AUTO_MODE = 0;
const MANUAL_MODE = 1;
const ALIGNMENT = {
    '-1': 'left',
    '0': 'center',
    '1': 'right'
};
const MIN_ICON_SIZE = 10;
const DEFAULT_MARGIN_RATIO = 0.2;
const DEFAULT_SPACING = 20;
const DEFAULT_SIZE = -1;
const DEFAULT_THEME = 100001000;
const DEFAULT_CUSTOM_THEME = 165001001;

const MODE_STYLE_KEY = 'wixsocialbar.mode';
const SIZE_STYLE_KEY = 'wixsocialbar.size';
const INDEX_STYLE_KEY = 'wixsocialbar.index.';
const SHOW_PLATFORM_STYLE_KEY = 'wixsocialbar.show.';
const MANUAL_SPACING_STYLE_KEY = 'wixsocialbar.manual.spacing';
const SPACING_STYLE_KEY = 'wixsocialbar.spacing';
const ALIGNMENT_STYLE_KEY = 'wixsocialbar.alignment';
const THEME_STYLE_KEY = 'wixsocialbar.theme';
const CUSTOM_THEME_STYLE_KEY = 'wixsocialbar.theme.custom';
const APPLIED_THEME_STYLE_KEY = 'wixsocialbar.theme.applied';
const IS_CUSTOM_THEME_STYLE_KEY = 'wixsocialbar.custom.theme.selected';

// ============================================================================
// SAGAS
// ============================================================================

const initState = ({window, Wix}) => function* () {
    let w = window || {};
    let width = w.innerWidth || MIN_WINDOW_WIDTH;
    let height = w.innerHeight || MIN_WINDOW_HEIGHT;

    Wix.Performance.applicationLoaded();

    yield put(actions.setViewMode({viewMode: Wix.Utils.getViewMode()}));
    yield put(actions.resizeApp({width, height}));
    yield put(actions.resizeAppEnding());

    yield call(setOnPageNavigationListener({Wix, window}));
};

const editModeChange = () => function* ({payload}) {
    yield put(actions.setViewMode({viewMode: payload.editMode}));
};

const stylesUpdate = ({Wix, window}) => function* ({payload}) {
    let { styles } = payload;

    if (styles) {
        yield call(setPropsFromStyle({Wix, window}), styles);
    }
};

const resizeApp = ({Wix, window}) => function* ({payload}) {
    let { width, height } = payload;
    let styles = yield select(styleSelector);

    yield put(actions.resizeAppSuccess({width, height}));
    if (styles) {
        yield call(setPropsFromStyle({Wix, window}), styles);
    }
    yield put(actions.ssrInit());
};

const resizeAppEnd = ({Wix, window}) => function* () {
    let styles = yield select(styleSelector);

    yield put(actions.resizeAppEnding());
    if (styles) {
        yield call(setPropsFromStyle({Wix, window}), styles);
    }
};

// ============================================================================
// MIDDLEWARES
// ============================================================================

const setPropsFromStyle = ({Wix, window}) => function* (styles) {
    let platformList = yield call(getPlatformList, styles);
    let { size, margin } = yield call(calcNewSize({Wix, window}), styles, platformList.length);
    let withWrap = yield call(shouldWrap, platformList.length, size, margin);
    let alignment = yield call(getAlignment, styles);
    let theme = yield call(getTheme, styles);

    yield put(actions.setProps({platformList, size, margin, withWrap, alignment, theme}));
};

const getPlatformList = function* (styles) {
    let platformList = [];
    let linkOrderData = {};

    _.forEach(SOCIAL_PLATFORMS, platform => {
        linkOrderData[platform] = styles.numbers[`${INDEX_STYLE_KEY}${platform}`];
    });

    let orderedPlatformList = _.keys(linkOrderData).sort((a, b) => linkOrderData[a] - linkOrderData[b]);

    for (let i = 0; i < orderedPlatformList.length; i++) {
        let platform = orderedPlatformList[i];
        if (styles.booleans[`${SHOW_PLATFORM_STYLE_KEY}${platform}`]) {
            platformList.push(platform);
        }
    }

    return platformList;
};

const calcNewSize = ({Wix, window}) => function* (styles, numOfPlatforms) {
    let mode = yield call(getMode({window}), styles);
    let result = {};

    switch (mode) {
        case MANUAL_MODE:
            let spacing = yield call(getManualSpacing, styles);
            let size = yield call(getManualSize, styles, numOfPlatforms, spacing);
            let margin = Math.floor((spacing / 100) * size);
            result = {margin, size};
            let isUpdating = yield select(isUpdatingSelector);
            let isResizing = yield select(isResizingSelector);
            if (!isUpdating && !isResizing) {
                yield spawn(updateComponentSizeIfNeeded({Wix}), size, spacing, numOfPlatforms);
            }
            break;
        case AUTO_MODE:
        default:
            result = yield call(calcAutoSize, numOfPlatforms);
            break;
    }

    return result;
};

const shouldWrap = function* (numOfPlatforms, size, margin) {
    let width = yield select(widthSelector);
    return (numOfPlatforms !== Math.floor((width + margin) / (size + margin)));
};

const getAlignment = function (styles) {
    return ALIGNMENT['' + (styles.numbers[ALIGNMENT_STYLE_KEY] || 0)];
};

const getTheme = function* (styles) {
    let viewMode = yield select(viewModeSelector);
    let appliedTheme = styles.numbers[APPLIED_THEME_STYLE_KEY];
    let isCustomSelected = styles.booleans[IS_CUSTOM_THEME_STYLE_KEY];
    let theme = {};

    if (viewMode === EDITOR_VIEW_MODE && appliedTheme !== 0) {
        theme = yield call(getAppliedThemeFromStyles, styles, appliedTheme);
    } else if (isCustomSelected) {
        theme = yield call(getCustomThemeFromStyles, styles);
    } else {
        theme = yield call(getThemeFromStyles, styles);
    }

    return theme;

};

const setOnPageNavigationListener = ({Wix, window}) => function* () {
    const navChannel = yield call(wixOnPageNavigationChannel, {Wix});
    yield takeEvery(navChannel, onNavigationHandler({window}));
};

const onNavigationHandler = ({window}) => function* () {
    let tries = 0;
    let width = $(window).outerWidth();
    let height = $(window).outerHeight();

    while (tries < 3 && (width === 0 || height === 0)) {
        yield delay(100);
        width = $(window).outerWidth();
        height = $(window).outerHeight();
        tries++;
    }

    yield put(actions.resizeApp({width, height}));
};

function wixOnPageNavigationChannel({Wix}) {
    return eventChannel(emitter => {
        Wix.addEventListener(Wix.Events.PAGE_NAVIGATION, onPageNavigation);

        return () => {
            Wix.removeEventListener(Wix.Events.PAGE_NAVIGATION, onPageNavigation);
        };

        function onPageNavigation(res) {
            emitter(res);
        }
    });
}

// ============================================================================
// HELPERS
// ============================================================================

const getMode = ({window}) => function* (styles) {
    let typeParam = 'socialbar.client';
    if (window) {
        typeParam = yield call(getQueryParameter({window}), 'type');
    }
    let mode = styles.numbers[MODE_STYLE_KEY];

    if (typeParam === 'socialbar.client.mobile') {
        mode = 0;
    }

    return mode;
};

const getQueryParameter = ({window}) => function (name) {
    let url = window.location.href;
    name = name.replace(/[\[\]]/g, '\\$&');
    var regex = new RegExp(`[?&]${name}(=([^&#]*)|&|#|$)`),
        results = regex.exec(url);
    if (!results) return null;
    if (!results[2]) return '';
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
};

const getManualSpacing = function* (styles) {
    let spacing = styles.numbers[MANUAL_SPACING_STYLE_KEY];

    return (spacing === -1) ? DEFAULT_SPACING : spacing;
};

const getManualSize = function* (styles, numOfPlatforms, spacing) {
    let size = styles.numbers[SIZE_STYLE_KEY];

    if (size === DEFAULT_SIZE) {
        let calc = yield call(calcAutoSize, numOfPlatforms, spacing);
        size = calc.size;
    }

    return size;
};

const calcAutoSize = function* (numOfPlatforms, spacing) {
    let width = yield select(widthSelector);
    let height = yield select(heightSelector);
    let oldSpacing = yield select(oldSpacingSelector);
    let defaultMarginRatio = (oldSpacing !== -1) ? (oldSpacing / 100) : DEFAULT_MARGIN_RATIO;
    let marginRatio = spacing ? spacing / 100 : defaultMarginRatio;
    let minDimension = Math.min(width, height);
    let size = (minDimension % 2) ? minDimension : minDimension - 1;

    for (; size > MIN_ICON_SIZE; size -= 1) {
        let isFitting = yield call(isSizeFitting, width, height, size, marginRatio, numOfPlatforms);
        if (isFitting) {
            break;
        }
    }

    size = size || MIN_ICON_SIZE;
    let margin = Math.floor(marginRatio * size);

    return {size, margin};
};

const isSizeFitting = function* (width, height, size, marginRatio, numOfPlatforms) {
    let result = false;
    let xAmount = yield call(extractAmount, width, size, marginRatio);
    let yAmount = yield call(extractAmount, height, size, marginRatio);
    let maxAmount = xAmount * yAmount;
    let rowAmount = numOfPlatforms % xAmount;
    let isOptimal = (xAmount > numOfPlatforms || xAmount - rowAmount <= 1 || rowAmount === 0) &&
        ((numOfPlatforms <= (2 * xAmount)) || rowAmount !== 1);

    if (isOptimal && maxAmount >= numOfPlatforms &&
        (maxAmount > numOfPlatforms || (numOfPlatforms > (maxAmount - xAmount)) && (numOfPlatforms > (maxAmount - yAmount)))) {
        result = true;
    }

    return result;
};

const extractAmount = function (dimension, size, marginRatio) {
    let margin = marginRatio * size;
    return Math.floor((dimension + margin) / (size + margin));
};

const getAppliedThemeFromStyles = function (styles, appliedTheme) {
    appliedTheme = (!appliedTheme || appliedTheme === -1) ? DEFAULT_THEME : appliedTheme;
    let theme = ThemeDecoder(appliedTheme);

    if ((theme.type !== SocialIcon.THEME_TYPES.GRAYSCALE) ||
        (theme.backgroundColor === 7 && theme.borderColor === 7 && theme.iconColor === 7)) {
        delete theme.backgroundColor;
        delete theme.iconColor;
        delete theme.borderColor;
    }

    return theme;
};

const getCustomThemeFromStyles = function (styles) {
    let themeId = styles.numbers[CUSTOM_THEME_STYLE_KEY];
    themeId = (!themeId || themeId === -1) ? DEFAULT_CUSTOM_THEME : themeId;
    return ThemeDecoder(themeId);
};

const getThemeFromStyles = function (styles) {
    let themeId = styles.numbers[THEME_STYLE_KEY];
    themeId = (!themeId || themeId === -1) ? DEFAULT_THEME : themeId;
    let theme = ThemeDecoder(themeId);

    delete theme.backgroundColor;
    delete theme.iconColor;
    delete theme.borderColor;

    return theme;
};

const updateComponentSizeIfNeeded = () => function* (size, spacingRatio, numOfPlatforms) {
    let width = yield select(widthSelector);
    let height = yield select(heightSelector);
    let margin = Math.floor((spacingRatio / 100) * size);

    let xAmount = Math.max(Math.floor((width + margin) / (size + margin)), 1);
    let yAmount = Math.max(Math.ceil(numOfPlatforms / xAmount), 1);

    let minWidth = (xAmount * (size + margin)) - margin;
    let minHeight = (yAmount * (size + margin)) - margin;

    if (minWidth > width || minHeight > height) {
        yield spawn(function* () {
            yield delay(50);
            yield put(actions.updateComponentSize({width: minWidth, height: minHeight}));
        });
    }
};

const updateComponentSize = ({Wix}) => function* ({payload}) {
    let { width, height } = payload;
    try {
        if (width && height) {
            yield put(actions.updatingComponentSize());
            yield call(sendUpdateSize({Wix}), {width, height});
        }
    } finally {
        yield put(actions.updatingComponentSizeEnd());
    }
};

const sendUpdateSize = ({Wix}) => function ({width, height}) {
    return new Promise((resolve, reject) => {
        Wix.resizeComponent({width, height}, res => {
            resolve(res);
        }, err => {
            reject(err);
        });
    });
};

// ============================================================================
// SELECTORS
// ============================================================================

const widthSelector = (state) => state.socialBar.width;
const heightSelector = (state) => state.socialBar.height;
const viewModeSelector = (state) => state.socialBar.viewMode;
const styleSelector = (state) => state.styles.styles;
const oldSpacingSelector = (state) => state.styles && state.styles.styles && state.styles.styles.numbers[SPACING_STYLE_KEY];
const isUpdatingSelector = (state) => state.socialBar.updatingComponentSize;
const isResizingSelector = (state) => state.socialBar.resizingApp;

// ============================================================================
// WATCHERS
// ============================================================================

const generateWatcher = (sagaParams, actionType, callback) => function* () {
    if (callback.takeLatest && _.isFunction(callback.fn)) {
        yield takeLatest(actionType, callback.fn(sagaParams));
    } else if (_.isFunction(callback)) {
        yield takeEvery(actionType, callback(sagaParams));
    } else if (callback.throttle && _.isFunction(callback.fn)) {
        while (true) {
            const payload = yield take(actionType);
            yield fork(callback.fn(sagaParams), payload);
            let throttle = _.isNumber(callback.throttle) ? callback.throttle : DEFAULT_THROTTLE;
            yield delay(throttle);
        }
    }
};

// ============================================================================
// DEFAULT SAGA EXPORT
// ============================================================================

export default function* socialBarSaga(sagaParams) {
    let forks = [];
    let definedActions = defineActions();

    _.forEach(definedActions, (callback, actionType) => {
        forks.push(fork(generateWatcher(sagaParams, actionType, callback)));
    });

    yield forks;
}

function defineActions() {
    return {
        [types.INIT_STATE]: initState,
        [types.RESIZE_APP]: resizeApp,
        [types.RESIZE_APP_END]: resizeAppEnd,
        [WixBridgeTypes.EDIT_MODE_CHANGE]: editModeChange,
        [StyleTypes.SET_STYLES]: stylesUpdate,
        [types.UPDATE_COMPONENT_SIZE]: updateComponentSize
    };
}



// WEBPACK FOOTER //
// ./sagas/SocialBarSaga.js